import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Euro, Calendar, Smartphone, CreditCard, FileText, Download, ExternalLink, Eye } from "lucide-react";
import { api } from "../lib/config";

const RULES = [
  { icon: Euro, title: "Quota iscrizione", desc: "60€ per l'intera stagione 2026/2027." },
  { icon: Calendar, title: "Durata", desc: "Stagione completa di Serie A, coppe e competizioni collaterali." },
  { icon: Smartphone, title: "App ufficiale", desc: "Fantagazzetta / Fantacalcio per voti, statistiche e formazione." },
  { icon: CreditCard, title: "Pagamenti", desc: "Bonifico, PayPal, Klarna, carta Visa/Mastercard." },
];

const BULLETS = [
  "Inserimento formazione entro la deadline ufficiale",
  "Voti basati sulle pagelle della Gazzetta dello Sport",
  "Classifica principale + 9 leghe collaterali",
  "Regolamento trasparente, assistenza community dedicata",
  "Progetto ufficialmente ASD (Associazione Sportiva Dilettantistica)",
];

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function RegolamentoFile() {
  const [meta, setMeta] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/regolamento")
      .then((r) => { if (alive) setMeta(r.data); })
      .catch(() => { if (alive) setMeta({ available: false }); });
    return () => { alive = false; };
  }, []);

  if (!meta || !meta.available) return null;

  const downloadUrl = `${api.defaults.baseURL}/regolamento/download`;
  const isPdf = meta.mime_type === "application/pdf";
  const isImage = (meta.mime_type || "").startsWith("image/");

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mt-12 p-7 md:p-9 rounded-3xl border border-fp-cyan/30 bg-gradient-to-br from-fp-cyan/10 via-fp-surface/40 to-fp-surface/40 backdrop-blur"
      data-testid="regolamento-download-card"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-6">
        <div className="w-16 h-16 rounded-2xl bg-fp-cyan/20 border border-fp-cyan/40 flex items-center justify-center text-fp-cyan flex-shrink-0">
          <FileText size={28} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">
            Documento ufficiale
          </div>
          <h3 className="mt-1 font-heading font-black text-xl md:text-2xl silver-gradient">
            Scarica il regolamento completo
          </h3>
          {meta.description && (
            <p className="mt-2 text-white/70 text-sm">{meta.description}</p>
          )}
          <div className="mt-3 flex items-center gap-3 text-xs text-white/50">
            <span className="font-mono">{meta.filename}</span>
            <span>·</span>
            <span>{formatBytes(meta.size_bytes)}</span>
            <span>·</span>
            <span className="uppercase">{(meta.mime_type || "").split("/")[1]}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          {(isPdf || isImage) && (
            <button
              onClick={() => setShowPreview(true)}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/15 text-white/80 hover:border-fp-cyan hover:text-fp-cyan transition-all font-heading font-bold text-sm uppercase tracking-wider"
              data-testid="regolamento-preview-btn"
            >
              <Eye size={16} /> Anteprima
            </button>
          )}
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={meta.filename}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-sm uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all"
            data-testid="regolamento-download-btn"
          >
            <Download size={16} /> Scarica
          </a>
        </div>
      </div>

      {showPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setShowPreview(false)}
          data-testid="regolamento-preview-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl h-[85vh] rounded-2xl overflow-hidden bg-fp-bg border border-white/10"
          >
            <div className="absolute top-3 right-3 z-10 flex gap-2">
              <a
                href={downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-fp-cyan text-fp-bg text-xs font-heading font-bold uppercase tracking-wider"
              >
                <ExternalLink size={14} /> Apri
              </a>
              <button
                onClick={() => setShowPreview(false)}
                className="px-3 py-2 rounded-lg bg-fp-surface/80 border border-white/10 text-white text-xs font-heading font-bold uppercase tracking-wider"
              >
                Chiudi
              </button>
            </div>
            {isPdf ? (
              <iframe
                src={downloadUrl}
                title="Regolamento"
                className="w-full h-full"
                data-testid="regolamento-pdf-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-black p-5">
                <img src={downloadUrl} alt="Regolamento" className="max-w-full max-h-full object-contain" />
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function Rules() {
  return (
    <section id="regolamento" className="relative py-24 md:py-32 px-5 md:px-8" data-testid="rules-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
            Edizione 2026/2027
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight silver-gradient">
            Come <span className="cyan-gradient">funziona</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="grid sm:grid-cols-2 gap-5">
            {RULES.map((r, i) => {
              const Icon = r.icon;
              return (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="p-6 rounded-2xl border border-white/10 bg-fp-surface/60 hover:border-fp-cyan/40 transition-all"
                  data-testid={`rule-${i}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-fp-cyan/10 border border-fp-cyan/30 flex items-center justify-center text-fp-cyan mb-4">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-heading font-bold text-lg">{r.title}</h3>
                  <p className="mt-2 text-white/60 text-sm">{r.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-10 rounded-3xl border border-white/10 bg-gradient-to-br from-fp-cyan/5 to-fp-surface/40 tracing-border"
          >
            <h3 className="font-heading font-black text-2xl md:text-3xl silver-gradient">
              Cosa include l'iscrizione
            </h3>
            <ul className="mt-6 space-y-4">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/80">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-fp-cyan/20 border border-fp-cyan flex items-center justify-center flex-shrink-0">
                    <Check size={12} className="text-fp-cyan" />
                  </span>
                  <span className="text-sm md:text-base">{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 pt-8 border-t border-white/10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-fp-cyan font-heading font-semibold">
                Totale
              </div>
              <div className="mt-2 flex items-end gap-2">
                <span className="font-heading font-black text-6xl silver-gradient leading-none">60</span>
                <span className="font-heading font-bold text-3xl text-white/60 mb-2">€</span>
                <span className="text-white/40 text-sm mb-3 ml-2">/ stagione</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Downloadable Regolamento file (managed from admin) */}
        <RegolamentoFile />
      </div>
    </section>
  );
}
